/**
 * The Roster store: the seam the Roster pages load and save through; pages know nothing about where the document comes from.
 * Only the stale-save half of the source app's sync is ported: a stale `PUT /api/roster` is refused with 409 and the planner reloads and reapplies; there is no client-side merge.
 * `reload()` is the explicit exit from a 409: `RosterSection` refuses to adopt the server's copy while the draft is dirty, so without it every retry resent the stale version.
 */
import { createContext, useContext } from 'react'
import type { SaveGate } from './saveGate'

import type { CellWrite, PlanDraft } from './scopedDraft'
import type { LayoutOp } from './layoutSave'
import type { RosterDb } from './types'

/** Roster staff or lead editing their project's plan on the published view; absent for a manager. Their edits never go through `update()`: each gesture is one PATCH. */
interface ScopedEdit {
  scope: readonly string[]
  draft: PlanDraft | undefined
  write: (writes: CellWrite[]) => Promise<boolean>
  saving: boolean
  version: number | undefined
}

/** True when the write landed, a refusal to show beside the button, or false when nothing was refused. */
export type PlanWriteAnswer = boolean | string

export interface RosterStore {
  db: RosterDb
  /**
   * Apply a change; the updater gets a structurally-shared draft and mutates it.
   * `mutate` is the replayable description of the edit (`pendingEdits`), so it must read nothing that can change between edit and replay: close over a value, never a live source.
   * `opts.replay: false` records the edit for undo only; `Schedule` keeps its own record of plan-grid paints and two mechanisms would apply it twice.
   */
  update: (
    mutate: (draft: RosterDb) => void,
    label: string,
    opts?: { replay?: boolean },
  ) => void
  /** Change the row layout: shown at once and saved on its own (`PATCH /api/roster/layout`), so it never makes the draft dirty. Optional so a test double need not provide it. */
  arrange?: (op: LayoutOp) => void
  /** Labels of the unsaved edits `reload()` would put back, oldest first, so the conflict banner can say what will happen. */
  pendingEdits: readonly string[]
  scoped?: ScopedEdit
  /** Held Publish / Revert edits a reload will NOT re-apply; non-empty only while there is something to warn about. */
  undo: () => void
  canUndo: boolean
  /** Undo-stack depth; a page notices it going DOWN to drop an undone edit from its own replay record. */
  undoDepth: number
  /** Put back the edit `undo` took away. Cleared by any new edit and by `reload()`: a redo snapshot belongs to a replaced lineage. */
  redo: () => void
  canRedo: boolean
  dirty: boolean
  /** Save now (the person card's one Save). Optional so a test double need not provide it. `quiet` skips the toast; resolves false when the server refused. */
  save?: (quiet?: boolean) => Promise<boolean | void>
  /** Saves unsaved Draft edits, then moves the Plan to the Draft up to `until`; resolves false on a refusal, which it has already said. */
  publish?: (until: string) => Promise<PlanWriteAnswer>
  /** Blanks the Plan on a range; the Draft is not touched. */
  clean?: (from: string, to: string) => Promise<PlanWriteAnswer>
  saving?: boolean
  lastLabel: string | null
  canEdit: boolean
  /** A stale (409) save not yet resolved. Sticky on purpose: it is a state the document is in, and `reload()` is the only useful action. */
  conflict: boolean
  /**
   * Take the server's copy and REPLAY the unsaved edits onto it, in one `update()` (one undo step).
   * ⚠️ Replay is not a merge: held mutators are re-run verbatim, so last write wins, and an append (`Setup`'s adds) can duplicate because its "already exists" guard lives in the handler.
   * ⛔ Also empties the undo stack: those snapshots are of the pre-reload lineage, and undoing then saving would send the stale draft at the new version and overwrite the other planner.
   */
  reload: () => Promise<void>
  /** A page holding an unsaved row registers it here; the bottom Save applies or refuses it. `null` unregisters. */
  setSaveGate?: (id: string, gate: SaveGate | null) => void
}

export const RosterContext = createContext<RosterStore | null>(null)

export function useRoster(): RosterStore {
  const store = useContext(RosterContext)
  if (!store) {
    throw new Error('useRoster must be used inside <RosterProvider>')
  }
  return store
}
